import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootReducersState } from 'redux/reducers';
import { DataContent, IProps } from './types';
import moment from 'moment';
import _ from 'lodash';
import { InsType } from 'constants/enums/insType.enum';
import { NationalType } from 'constants/enums/nationalType.enum';
import commonService from 'utils/commonService';

const InsuredInfo: React.FC<IProps> = (props) => {
  const [mobileProtected, setMobileProtected] = useState<boolean[]>([]);
  const [addressProtected, setAddressProtected] = useState<boolean[]>([]);
  const [idProtected, setIdProtected] = useState<boolean[]>([]);
  const resources = useSelector((state: RootReducersState) => state.resources);

  /**
   * @description 資料顯示或隱藏狀態初始化
   */
  useEffect(() => {
    if (props.DATA) {
      setMobileProtected([...Array(props.DATA.length)].map(() => true));
      setAddressProtected([...Array(props.DATA.length)].map(() => true));
      setIdProtected([...Array(props.DATA.length)].map(() => true));
    }
  }, [props.DATA]);

  /**
   * @description 處理手機資料顯示或隱藏切換
   */
  const handleMobileViewToggle = (targetIndex: number) => {
    const mobileProtectedUpdated = [...mobileProtected];
    mobileProtectedUpdated[targetIndex] = !mobileProtected[targetIndex];
    setMobileProtected(mobileProtectedUpdated);
  };

  /**
   * @description 處理地址資料顯示或隱藏切換
   */
  const handleAddressViewToggle = (targetIndex: number) => {
    const addressProtectedUpdated = [...addressProtected];
    addressProtectedUpdated[targetIndex] = !addressProtected[targetIndex];
    setAddressProtected(addressProtectedUpdated);
  };

  /**
   * @description 處理身份證字號(新式統一證號)資料顯示或隱藏切換
   */
  const handleIdViewToggle = (targetIndex: number) => {
    const idProtectedUpdated = [...idProtected];
    idProtectedUpdated[targetIndex] = !idProtected[targetIndex];
    setIdProtected(idProtectedUpdated);
  };

  /**
   * @description 建立 多 被保人表格-PC
  */
  const handleCreateInsuredInfo = (datas: DataContent[]) => {
    return _.map(datas, (data: DataContent, index: number) => {
      return (
        <tr key={index}>
          <td>{commonService.formatNullToDash(data.SEQNO)}</td>
          <td>{commonService.formatNullToDash(data.RELATIONSHIP)}</td>
          <td>{commonService.formatNullToDash(data.NAME)}</td>
          <td>
            {!idProtected[index]
              ? commonService.formatNullToDash(data.ID)
              : commonService.hiddenStringLastCharacter(commonService.formatNullToDash(data.ID), 5)}
            {data.ID && (
              <button type="button" className="element__view-btn" onClick={() => handleIdViewToggle(index)}>
                {!idProtected[index] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
              </button>
            )}
          </td>
          <td>{commonService.formatNullToDash(data.GENDER)}</td>
          <td>{data.BIRTHDAY ? moment(data.BIRTHDAY).format('YYYY/MM/DD') : '-'}</td>
          <td>
            {!mobileProtected[index]
              ? commonService.formatMobileNumAddDash(data.MOBILE)
              : commonService.hiddenStringCharactersByIndexRange(commonService.formatMobileNumAddDash(data.MOBILE), 5, 8)}
            {data.MOBILE && (
              <button type="button" className="element__view-btn" onClick={() => handleMobileViewToggle(index)}>
                {!mobileProtected[index] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
              </button>
            )}
          </td>
          <td>{commonService.formatNullToDash(data.EMAIL_ADDR)}</td>
        </tr>
      );
    });
  };

  /**
   * @description 建立 多 被保人表格-Mobile
  */
  const handleCreateMobileInsuredInfo = (datas: DataContent[]) => {
    return _.map(datas, (data: DataContent, index: number) => {
      return React.cloneElement(
        <>
          <tr>
            <td rowSpan={2}>{commonService.formatNullToDash(data.SEQNO)}</td>
            <td rowSpan={2}>{commonService.formatNullToDash(data.NAME)}</td>
            <td rowSpan={2}>{commonService.formatNullToDash(data.GENDER)}</td>
            <td>{data.BIRTHDAY ? moment(data.BIRTHDAY).format('YYYY/MM/DD') : '-'}</td>
          </tr>
          <tr>
            <td>
              {!mobileProtected[index]
                ? commonService.formatMobileNumAddDash(data.MOBILE)
                : commonService.hiddenStringCharactersByIndexRange(commonService.formatMobileNumAddDash(data.MOBILE), 5, 8)}
              {data.MOBILE && (
                <button type="button" className="element__view-btn" onClick={() => handleMobileViewToggle(index)}>
                  {!mobileProtected[index] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
                </button>
              )}
            </td>
          </tr>
          <tr>
            <td colSpan={4}>{commonService.formatNullToDash(data.RELATION)}</td>
          </tr>
          <tr>
            <td colSpan={4}>
              {!idProtected[index]
                ? commonService.formatNullToDash(data.ID)
                : commonService.hiddenStringLastCharacter(commonService.formatNullToDash(data.ID), 5)}
              {data.ID && (
                <button type="button" className="element__view-btn" onClick={() => handleIdViewToggle(index)}>
                  {!idProtected[index] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
                </button>
              )}
            </td>
          </tr>
          <tr>
            <td colSpan={4}>{commonService.formatNullToDash(data.EMAIL_ADDR)}</td>
          </tr>
        </>
        , { key: index }
      );
    });
  };

  return (
    <div className="insured-info">
      <div className="insured-info__title">{commonService.getNameByLng(resources, 'TID_Insured_information')}</div>
      <div className="insured-info__content">
        {/* 單筆 */}
        {
          props.DATA && props.DATA.length === 1 && (
            <div className="insured-info__list">
              <div className="element__list">
                <div className="element__list-col">
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Document_type')}</div>
                    <div className="element__list-group-content">
                      {props.DATA[0].INSURED_ID_TYPE_TEXT || props.DATA[0].NATIVE || props.DATA[0].NATIONALITY_TYPE || '-'}
                    </div>
                  </div>
                </div>
                <div className="element__list-col">
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Name')}</div>
                    <div className="element__list-group-content">{commonService.formatNullToDash(props.DATA[0].NAME)}</div>
                  </div>
                </div>
                {
                  props.DATA[0].INSURED_ID_TYPE !== NationalType.Legal &&
                  props.DATA[0].NATIONALITY_TYPE !== NationalType.Legal && (
                    <div className="element__list-col">
                      <div className="element__list-group">
                        <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Birth_date')}</div>
                        <div className="element__list-group-content">{props.DATA[0].BIRTHDAY ? moment(props.DATA[0].BIRTHDAY).format('YYYY/MM/DD') : '-'}</div>
                      </div>
                    </div>
                  )
                }
                <div className="element__list-col">
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Phone_number')}</div>
                    <div className="element__list-group-content">
                      {!mobileProtected[0]
                        ? commonService.formatMobileNumAddDash(commonService.formatNullToDash(props.DATA[0].MOBILE))
                        : commonService.hiddenStringCharactersByIndexRange(commonService.formatMobileNumAddDash(commonService.formatNullToDash(props.DATA[0].MOBILE)), 5, 8)}
                      {props.DATA[0].MOBILE && (
                        <button type="button" className="element__view-btn" onClick={() => handleMobileViewToggle(0)}>
                          {!mobileProtected[0] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="element__list-col">
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Address')}</div>
                    <div className="element__list-group-content">
                      {!addressProtected[0] && (props.DATA[0].VEHICLE_ADDRESS?.FULL || props.DATA[0].ADDRESS || '-')}
                      {addressProtected[0] && ((props.DATA[0].VEHICLE_ADDRESS?.FULL && commonService.hiddenStringCharactersByIndexRange(props.DATA[0].VEHICLE_ADDRESS?.FULL, 13, 19)) || (props.DATA[0].ADDRESS && commonService.hiddenStringCharactersByIndexRange(props.DATA[0].ADDRESS, 13, 19)) || '-')}
                      {((props.DATA[0].VEHICLE_ADDRESS?.FULL && props.DATA[0].VEHICLE_ADDRESS?.FULL.length > 13) || (props.DATA[0].ADDRESS && props.DATA[0].ADDRESS.length > 13)) && (
                        <button type="button" className="element__view-btn" onClick={() => handleAddressViewToggle(0)}>
                          {!addressProtected[0] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className={'element__list-col' + (resources.lng === 'en' ? ' element__list-col--full' : '')}>
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_ID_New_UI_number')}</div>
                    <div className="element__list-group-content">
                      {!idProtected[0]
                        ? commonService.formatNullToDash(props.DATA[0].ID)
                        : commonService.hiddenStringLastCharacter(commonService.formatNullToDash(props.DATA[0].ID), 5)}
                      {props.DATA[0].ID && (
                        <button type="button" className="element__view-btn" onClick={() => handleIdViewToggle(0)}>
                          {!idProtected[0] ? <i className="fas fa-eye" /> : <i className="fas fa-eye-slash" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {
                  props.DATA[0].INSURED_ID_TYPE !== NationalType.Legal &&
                  props.DATA[0].NATIONALITY_TYPE !== NationalType.Legal && (
                    <div className={'element__list-col' + (resources.lng === 'en' ? ' element__list-col--full' : '')}>
                      <div className="element__list-group">
                        <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Gender')}</div>
                        <div className="element__list-group-content">{commonService.formatNullToDash(props.DATA[0].GENDER)}</div>
                      </div>
                    </div>
                  )
                }
                {
                  props.InsTypeId !== InsType.Fire && props.InsTypeId !== InsType.MobileDevice && props.InsTypeId !== InsType.Pet &&
                  props.DATA[0].INSURED_ID_TYPE !== NationalType.Legal &&
                  props.DATA[0].NATIONALITY_TYPE !== NationalType.Legal && (
                    <div className={'element__list-col' + (resources.lng === 'en' ? ' element__list-col--full' : '')}>
                      <div className="element__list-group">
                        <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_Marital_status')}</div>
                        <div className="element__list-group-content">
                          {commonService.formatNullToDash(props.DATA[0].MARRIAGE_TEXT)}
                        </div>
                      </div>
                    </div>
                  )
                }
                <div className={'element__list-col' + (resources.lng === 'en' ? ' element__list-col--full' : '')}>
                  <div className="element__list-group">
                    <div className="element__list-group-title">{commonService.getNameByLng(resources, 'TID_EMail_address')}</div>
                    <div className="element__list-group-content">
                      {commonService.formatNullToDash(props.DATA[0].EMAIL_ADDR)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {/* 多筆 */}
        {
          props.DATA && props.DATA.length > 1 && (
            <>
              <div className="insured-info__table">
                <table>
                  <tbody>
                    <tr>
                      <th>{commonService.getNameByLng(resources, 'TID_SN')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Relationship_with_proposer')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Name')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_ID_New_UI_number')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Gender')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Birth_date')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Phone_number')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_EMail_address')}</th>
                    </tr>
                    {
                      handleCreateInsuredInfo(props.DATA)
                    }
                  </tbody>
                </table>
              </div>
              <div className="insured-info__table-mobile">
                <table>
                  <thead>
                    <tr>
                      <th rowSpan={2}>{commonService.getNameByLng(resources, 'TID_SN')}</th>
                      <th rowSpan={2}>{commonService.getNameByLng(resources, 'TID_Name')}</th>
                      <th rowSpan={2}>{commonService.getNameByLng(resources, 'TID_Gender')}</th>
                      <th>{commonService.getNameByLng(resources, 'TID_Birth_date')}</th>
                    </tr>
                    <tr>
                      <th>{commonService.getNameByLng(resources, 'TID_Phone_number')}</th>
                    </tr>
                    <tr>
                      <th colSpan={4}>{commonService.getNameByLng(resources, 'TID_Relationship_with_proposer')}</th>
                    </tr>
                    <tr>
                      <th colSpan={4}>{commonService.getNameByLng(resources, 'TID_ID_New_UI_number')}</th>
                    </tr>
                    <tr>
                      <th colSpan={4}>{commonService.getNameByLng(resources, 'TID_EMail_address')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      handleCreateMobileInsuredInfo(props.DATA)
                    }
                  </tbody>
                </table>
              </div>
            </>
          )
        }
      </div>
    </div>
  );
};

export default InsuredInfo;
